import { sectionRenderer } from '@theme/section-renderer';

/**
 * Switch that adds / removes the Shipping Protection product from the cart.
 * The switch state is rendered by Liquid from the cart contents, so it stays correct
 * after every cart re-render.
 */
class ShippingProtection extends HTMLElement {
  static observedAttributes = ['data-orphan'];

  #busy = false;

  connectedCallback() {
    this.toggle = this.querySelector('[data-protect-toggle]');
    this.toggle?.addEventListener('change', this.#onChange);

    this.#dropIfOrphaned();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback() {
    // The cart re-render can update this element in place instead of replacing it.
    this.#dropIfOrphaned();
  }

  /** Protection on its own is not an order: drop it when the last real item leaves the cart. */
  #dropIfOrphaned() {
    if (this.dataset.orphan === 'true') this.#update(false);
  }

  #onChange = () => {
    this.#update(Boolean(this.toggle?.checked));
  };

  /**
   * @param {boolean} on
   */
  async #update(on) {
    const variantId = Number(this.dataset.variantId);
    const sectionId = this.closest('cart-items-component')?.dataset.sectionId;
    if (!variantId || this.#busy) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = on
        ? await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          })
        : await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity: 0 }),
          });

      if (!response.ok) throw new Error('Shipping protection could not be updated');

      if (sectionId) await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (error) {
      console.error(error);
      if (this.toggle) this.toggle.checked = !on;
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}
